import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input, Label } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Calculator, MapPin, ChevronDown, ChevronRight, Info } from "lucide-react";
import { formatNum, apiErr } from "../lib/utils";
import { toast } from "sonner";

function RegistryBadge({ f }) {
  if (f.basis === "spend") return <Badge variant="warning" className="text-[10px]" data-testid="registry-badge-spend">EEIO · spend</Badge>;
  if (f.registry === "SEFR") return <Badge variant="lime" className="text-[10px]" data-testid="registry-badge-sefr">SEFR · SG</Badge>;
  if (f.registry === "IPCC") return <Badge variant="outline" className="text-[10px]" data-testid="registry-badge-ipcc">IPCC · intl</Badge>;
  return <Badge variant="outline" className="text-[10px]">{f.registry || "—"}</Badge>;
}

function FactorValue({ f }) {
  if (f.value_status === "pending" || f.factor_kgco2e === null || f.factor_kgco2e === undefined)
    return <span className="text-[11px] text-muted-foreground italic" data-testid="value-pending">value pending</span>;
  return <span className="font-mono">{f.factor_kgco2e} <span className="text-muted-foreground text-xs">/{f.unit}</span></span>;
}

export default function Emissions() {
  const [factors, setFactors] = useState([]);
  const [facets, setFacets] = useState({ registries: [], categories: [], pending_count: 0, total: 0 });
  const [scope, setScope] = useState("all");
  const [registry, setRegistry] = useState("all");
  const [category, setCategory] = useState("all");
  const [basis, setBasis] = useState("all");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});
  const [calc, setCalc] = useState({ factor_code: "", value: "" });
  const [result, setResult] = useState(null);
  const [rez, setRez] = useState({ category: "Fuel", activity: "", site_country: "Singapore" });
  const [rezOut, setRezOut] = useState(null);

  const load = async () => {
    const params = {};
    if (scope !== "all") params.scope = parseInt(scope);
    if (registry !== "all") params.registry = registry;
    if (category !== "all") params.category = category;
    if (basis !== "all") params.basis = basis;
    if (q) params.q = q;
    const { data } = await api.get("/emissions/factors", { params });
    setFactors(data.factors);
  };

  useEffect(() => {
    api.get("/emissions/factors/facets").then((r) => setFacets(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [scope, registry, category, basis]); // eslint-disable-line

  const calculate = async () => {
    if (!calc.factor_code || !calc.value) return;
    try {
      const { data } = await api.post("/emissions/calculate", { factor_code: calc.factor_code, value: parseFloat(calc.value) });
      setResult(data);
    } catch (e) { toast.error(apiErr(e, "Could not calculate")); }
  };

  const runResolve = async () => {
    try {
      const { data } = await api.post("/emissions/factors/resolve", {
        category: rez.category || null, activity: rez.activity || null, site_country: rez.site_country || null,
      });
      setRezOut(data);
    } catch (e) { toast.error(apiErr(e, "Could not resolve")); }
  };

  const sefrCount = facets.registries.find((r) => r.registry === "SEFR")?.count || 0;
  const ipccCount = facets.registries.find((r) => r.registry === "IPCC")?.count || 0;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div>
        <div className="label-eyebrow mb-1">Module</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Emission Factor Registry</h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
          Two tiers, location-aware. <span className="text-foreground font-medium">SEFR · Singapore</span> factors apply first at
          Singapore sites; the closest <span className="text-foreground font-medium">IPCC · international</span> default applies elsewhere or when SEFR has no match.
        </p>
        <div className="flex items-center gap-2 mt-3 text-xs" data-testid="registry-summary">
          <Badge variant="lime">SEFR · Singapore · {sefrCount}</Badge>
          <Badge variant="outline">IPCC · international · {ipccCount}</Badge>
          {facets.spend_count > 0 && <Badge variant="warning" data-testid="spend-summary-badge">Spend-based · EEIO · {facets.spend_count}</Badge>}
          {facets.pending_count > 0 && <span className="text-muted-foreground">· {facets.pending_count} value pending</span>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-flat">
          <div className="p-4 border-b border-border flex flex-wrap items-center gap-3 bg-surface">
            <Input placeholder="Search code, activity, category…" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()} className="max-w-xs" data-testid="factor-search" />
            <Select value={registry} onValueChange={setRegistry}>
              <SelectTrigger className="w-36" data-testid="registry-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All registries</SelectItem>
                <SelectItem value="SEFR">SEFR · Singapore</SelectItem>
                <SelectItem value="IPCC">IPCC · international</SelectItem>
              </SelectContent>
            </Select>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="w-28" data-testid="scope-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All scopes</SelectItem>
                <SelectItem value="1">Scope 1</SelectItem>
                <SelectItem value="2">Scope 2</SelectItem>
                <SelectItem value="3">Scope 3</SelectItem>
              </SelectContent>
            </Select>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-44" data-testid="category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All categories</SelectItem>
                {facets.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={basis} onValueChange={setBasis}>
              <SelectTrigger className="w-44" data-testid="basis-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All factor types</SelectItem>
                <SelectItem value="activity">Activity-based</SelectItem>
                <SelectItem value="spend">Spend-based (EEIO)</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={load} data-testid="factor-search-btn">Search</Button>
          </div>
          <div className="max-h-[560px] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Registry</TableHead>
                  <TableHead>Activity</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead className="text-right">kgCO2e / unit</TableHead>
                  <TableHead>Standard</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {factors.map((f) => (
                  <React.Fragment key={f.code}>
                    <TableRow data-testid="factor-row" className="cursor-pointer" onClick={() => setExpanded((e) => ({ ...e, [f.code]: !e[f.code] }))}>
                      <TableCell className="text-muted-foreground">{expanded[f.code] ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}</TableCell>
                      <TableCell><RegistryBadge f={f} /></TableCell>
                      <TableCell>
                        <div className="font-medium leading-tight">{f.name}</div>
                        <div className="text-[11px] text-muted-foreground font-mono">{f.code}{f.sub_category ? ` · ${f.sub_category}` : ""}</div>
                        {f.basis === "spend" && <Badge variant="warning" className="text-[9px] mt-0.5" data-testid="factor-lower-quality">Spend-based · lower-quality estimate · tier {f.data_quality_tier || "D"}</Badge>}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{f.category}</TableCell>
                      <TableCell><Badge variant="outline">S{f.scope}</Badge></TableCell>
                      <TableCell className="text-right"><FactorValue f={f} /></TableCell>
                      <TableCell className="text-[11px] text-muted-foreground">{f.ipcc_version || f.standard_applied || "—"}</TableCell>
                    </TableRow>
                    {expanded[f.code] && (
                      <TableRow className="bg-surface/50" data-testid="factor-detail">
                        <TableCell></TableCell>
                        <TableCell colSpan={6} className="text-xs text-muted-foreground space-y-1 py-3">
                          <div><span className="text-foreground font-medium">Source:</span> {f.source || f.issuing_body || "—"}</div>
                          {f.description && <div><span className="text-foreground font-medium">Note:</span> {f.description}</div>}
                          {f.boundary_inclusion && <div><span className="text-foreground font-medium">Boundary — included:</span> {f.boundary_inclusion}</div>}
                          {f.boundary_exclusion && <div><span className="text-foreground font-medium">Boundary — excluded:</span> {f.boundary_exclusion}</div>}
                          {f.is_gwp && <div className="text-accent">Global-warming-potential value (CO₂e per kg of gas).</div>}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
                {factors.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">No factors match your filters.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className="space-y-6">
          {/* Resolver preview */}
          <div className="card-flat p-6" data-testid="resolver-preview">
            <div className="flex items-center gap-2 mb-1"><MapPin className="h-5 w-5" /><h3 className="font-display font-semibold text-lg">Which factor applies?</h3></div>
            <p className="text-xs text-muted-foreground mb-4">Preview the location-aware pick before entering data.</p>
            <div className="space-y-3">
              <div>
                <Label>Category</Label>
                <Select value={rez.category} onValueChange={(v) => setRez({ ...rez, category: v })}>
                  <SelectTrigger data-testid="resolve-category"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">{facets.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Activity (optional)</Label><Input value={rez.activity} onChange={(e) => setRez({ ...rez, activity: e.target.value })} placeholder="e.g. Diesel, R-410A" data-testid="resolve-activity" /></div>
              <div><Label>Site country</Label><Input value={rez.site_country} onChange={(e) => setRez({ ...rez, site_country: e.target.value })} placeholder="e.g. Singapore" data-testid="resolve-country" /></div>
              <Button variant="primary" className="w-full" onClick={runResolve} data-testid="resolve-btn">Resolve factor</Button>
              {rezOut && (
                <div className="card-flat p-4 bg-accent/5 border-accent/30 space-y-1.5" data-testid="resolve-result">
                  {rezOut.resolved ? (
                    <>
                      <div className="font-medium text-sm">{rezOut.factor.name}</div>
                      <div className="text-[11px] font-mono text-muted-foreground">{rezOut.factor.code} · {rezOut.factor.factor_kgco2e} kgCO₂e/{rezOut.factor.unit}</div>
                      <Badge variant={rezOut.resolution.registry === "SEFR" ? "lime" : "outline"} className="text-[10px]">{rezOut.resolution.label}</Badge>
                      {rezOut.resolution.fallback_reason && <div className="flex items-start gap-1 text-[11px] text-accent"><Info className="h-3 w-3 mt-0.5 shrink-0" /><span>Fell back to IPCC: {rezOut.resolution.fallback_reason}</span></div>}
                      {rezOut.resolution.boundary_note && <div className="text-[10px] text-muted-foreground">{rezOut.resolution.boundary_note}</div>}
                    </>
                  ) : <div className="text-xs text-muted-foreground">No matching factor in either registry.</div>}
                </div>
              )}
            </div>
          </div>

          {/* Quick calculator */}
          <div className="card-flat p-6">
            <div className="flex items-center gap-2 mb-4"><Calculator className="h-5 w-5" /><h3 className="font-display font-semibold text-lg">Quick Calculator</h3></div>
            <div className="space-y-3">
              <div>
                <Label>Emission factor</Label>
                <Select value={calc.factor_code} onValueChange={(v) => setCalc({ ...calc, factor_code: v })}>
                  <SelectTrigger data-testid="calc-factor"><SelectValue placeholder="Pick a factor" /></SelectTrigger>
                  <SelectContent className="max-h-72">{factors.filter((f) => f.factor_kgco2e != null).slice(0, 300).map((f) => <SelectItem key={f.code} value={f.code}>{f.code} — {f.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Activity value</Label><Input type="number" step="any" value={calc.value} onChange={(e) => setCalc({ ...calc, value: e.target.value })} placeholder="e.g. 1200000" data-testid="calc-value" /></div>
              <Button variant="primary" className="w-full" onClick={calculate} disabled={!calc.factor_code || !calc.value} data-testid="calc-btn">Calculate</Button>
              {result && (
                <div className="card-flat p-4 bg-accent/5 border-accent/30 space-y-2" data-testid="calc-result">
                  <div className="text-xs text-muted-foreground font-mono">{result.factor_name}</div>
                  <div className="font-display text-3xl font-bold">{formatNum(result.co2e_tonnes, { decimals: 4 })}<span className="text-sm text-muted-foreground ml-1">tCO2e</span></div>
                  <div className="text-xs font-mono text-muted-foreground">{result.input_value} {result.input_unit} × {result.factor_kgco2e} kgCO2e</div>
                  <div className="flex items-center gap-2">
                    <Badge variant={result.registry === "SEFR" ? "lime" : "outline"} className="text-[10px]">{result.registry}{result.region ? ` · ${result.region === "SG" ? "SG" : "intl"}` : ""}{result.ipcc_version ? ` · ${result.ipcc_version}` : ""}</Badge>
                    <Badge variant="outline" className="text-[10px]">Scope {result.scope}</Badge>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
